import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { EntityContext } from "./EntityContext";
import { quoteNotesApi } from "@/lib/quote-notes";
import { jobsApi } from "@/lib/jobs";

export interface EntitySpecialNotesTabProps {
  context: EntityContext;
}

interface NormalizedNotes { text: string | null; rowVersionBase64: string | null; }

async function fetchNotes(ctx: EntityContext): Promise<NormalizedNotes> {
  if (ctx.kind === "quote") {
    const r = await quoteNotesApi.getNotes(ctx.id);
    return { text: r.note, rowVersionBase64: r.rowVersionBase64 };
  }
  const r = await jobsApi.getNotes(ctx.id);
  return { text: r.note, rowVersionBase64: r.rowVersionBase64 };
}

async function putNotes(ctx: EntityContext, body: { note: string; rowVersionBase64: string }) {
  return ctx.kind === "quote"
    ? quoteNotesApi.putNotes(ctx.id, body)
    : jobsApi.putNotes(ctx.id, body);
}

export function EntitySpecialNotesTab({ context }: EntitySpecialNotesTabProps) {
  const qc = useQueryClient();

  const notes = useQuery({ queryKey: [context.kind, context.id, "notes"], queryFn: () => fetchNotes(context) });

  const [noteDraft, setNoteDraft] = useState("");

  useEffect(() => { setNoteDraft(notes.data?.text ?? ""); }, [notes.data?.text]);

  const saveNotes = useMutation({
    mutationFn: () => putNotes(context, { note: noteDraft, rowVersionBase64: notes.data?.rowVersionBase64 ?? "" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [context.kind, context.id, "notes"] }); toast.success("Notes saved"); },
    onError:   () => toast.error("Failed to save notes"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Label htmlFor="notes" className="sr-only">Notes</Label>
          <Textarea
            id="notes"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={5}
            placeholder="Add notes…"
          />
          <div className="flex justify-end">
            <Button onClick={() => saveNotes.mutate()} disabled={saveNotes.isPending || notes.isLoading}>
              {saveNotes.isPending ? "Saving…" : "Save Notes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
